/**
 * AssignmentPicker
 * The one "Interna (n) / Externa (n)" tab UI for assigning a work order, shared
 * by AssignWorkOrderModal (existing work order) and CreateWorkOrderModal (the
 * create form). Presentational: the parent owns the selections and performs
 * the writes. Each tab renders an optional `current*` slot above its picker
 * so the modal can list the current assignees/delegates.
 */
import React from 'react';
import { Form, Nav } from 'react-bootstrap';
import InternalUserSelector from './InternalUserSelector';
import ExternalUserSelector from './ExternalUserSelector';
import { Userprofile } from '../Helpers/UsersContext';
import { ExternalUser } from '../types/guestAccess';

export type AssignmentTab = 'internal' | 'external';

export interface AssignmentPickerProps {
    activeTab: AssignmentTab;
    onTabChange: (tab: AssignmentTab) => void;
    internalCount?: number;
    externalCount?: number;
    /** Rendered at the top of the Interna tab (current assignees). */
    currentInternal?: React.ReactNode;
    /** Rendered at the top of the Externa tab (current delegates). */
    currentExternal?: React.ReactNode;
    /** Hide the add controls (work order no longer open). */
    readOnly?: boolean;

    // Internal
    internalUsers: Userprofile[];
    selectedInternalUser: string;
    onInternalUserChange: (uid: string) => void;
    /** UIDs greyed out in the picker ("Redan tilldelad"). */
    alreadyAssignedIds?: string[];
    internalUnassignedLabel?: string;
    /** Rendered next to the internal picker (e.g. an "Lägg till" button). */
    internalAction?: React.ReactNode;
    sendEmail: boolean;
    onSendEmailChange: (value: boolean) => void;

    // External
    externalUsers?: ExternalUser[];
    selectedExternalUser: ExternalUser | null;
    onExternalUserChange: (user: ExternalUser) => void;
    onCreateExternalUser: () => void;
    alreadyDelegatedIds?: string[];
    /** Rendered under the external picker when a user is selected (composer). */
    externalExtra?: React.ReactNode;

    disabled?: boolean;
    idSuffix?: string;
}

const AssignmentPicker: React.FC<AssignmentPickerProps> = ({
    activeTab,
    onTabChange,
    internalCount = 0,
    externalCount = 0,
    currentInternal,
    currentExternal,
    readOnly = false,
    internalUsers,
    selectedInternalUser,
    onInternalUserChange,
    alreadyAssignedIds,
    internalUnassignedLabel = 'Välj användare…',
    internalAction,
    sendEmail,
    onSendEmailChange,
    externalUsers,
    selectedExternalUser,
    onExternalUserChange,
    onCreateExternalUser,
    alreadyDelegatedIds,
    externalExtra,
    disabled = false,
    idSuffix = 'workorder',
}) => (
    <>
        <Nav variant="tabs" className="mb-3" role="tablist">
            <Nav.Item>
                <Nav.Link role="tab" active={activeTab === 'internal'} onClick={() => onTabChange('internal')}>
                    Interna ({internalCount})
                </Nav.Link>
            </Nav.Item>
            <Nav.Item>
                <Nav.Link role="tab" active={activeTab === 'external'} onClick={() => onTabChange('external')}>
                    Externa ({externalCount})
                </Nav.Link>
            </Nav.Item>
        </Nav>

        {activeTab === 'internal' && (
            <Form.Group>
                {currentInternal}
                {!readOnly && (
                    <>
                        <Form.Label>Lägg till intern mottagare</Form.Label>
                        <div className="d-flex gap-2">
                            <InternalUserSelector
                                users={internalUsers}
                                value={selectedInternalUser}
                                onChange={onInternalUserChange}
                                disabled={disabled}
                                disabledIds={alreadyAssignedIds}
                                unassignedLabel={internalUnassignedLabel}
                            />
                            {internalAction}
                        </div>
                        <Form.Check
                            type="checkbox"
                            id={`assign-send-email-${idSuffix}`}
                            className="mt-3"
                            label="Skicka e-postavisering till mottagaren"
                            checked={sendEmail}
                            onChange={(e) => onSendEmailChange(e.target.checked)}
                            disabled={disabled}
                        />
                    </>
                )}
            </Form.Group>
        )}

        {activeTab === 'external' && (
            <>
                {currentExternal}
                {!readOnly && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Lägg till extern mottagare</Form.Label>
                            <ExternalUserSelector
                                onSelect={onExternalUserChange}
                                onCreateNew={onCreateExternalUser}
                                selectedUser={selectedExternalUser}
                                users={externalUsers}
                                disabledIds={alreadyDelegatedIds}
                            />
                        </Form.Group>
                        {selectedExternalUser && externalExtra}
                    </>
                )}
            </>
        )}
    </>
);

export default AssignmentPicker;
